/**
 * RaidMode — the consumption half of the loop, and the thing that produces
 * content for the other half.
 *
 * Note what this file records and shows. It is not a score. It is a *timeline*:
 * the taunt you read before you went in, the moment a plate turned out to be
 * paint, the moment a spring took the decision away from you. Those captions
 * are the same events that get written into the replay, so the clip the owner
 * watches later is narrated by exactly what the raider felt.
 */

import { encodeRoom } from '../core/codec'
import type { RoomDef } from '../core/room'
import { RoomView } from '../render/roomView'
import type { SceneView } from '../render/scene'
import { createFigure, RAIDER_PALETTE } from '../render/character'
import type { Figure } from '../render/figure'
import { EVENT_SFX, sfx } from '../audio/sfx'
import { RaidSim } from '../sim/raid'
import type { RaidEvent, SimSnapshot } from '../sim/types'
import { ReplayRecorder, type Replay } from '../sim/replay'
import { ROOM_CONFIG } from '../core/room'
import { platform } from '../platform/crazygames'
import { button, el, formatSeconds } from '../ui/dom'
import { RaidControls } from './input'

export interface RaidModeDeps {
  scene: SceneView
  uiHost: HTMLElement
  room: RoomDef
  raiderName: string
  /** `playtest` runs are not filed as reactions — you can't prank yourself. */
  playtest?: boolean
  onFinish: (outcome: 'escaped' | 'failed', replay: Replay) => void
  onQuit: () => void
}

/** Short, past-tense, shareable. These are caption cards, not log lines. */
const CAPTIONS: Partial<Record<RaidEvent['type'], string>> = {
  launched: '被弹飞了！',
  swatted: '被拍了一下',
  pushed: '风把你推歪了',
  slipped: '刹不住',
  glued: '粘住了……',
  pitfall: '掉下去了',
  decoy: '这是个假宝箱',
  bluffed: '……这块板子是假的',
  pickup: '拿到了！快跑',
  escaped: '带出去了',
}

const INTRO_SECONDS = 2.2

/** Metres of ground travel per footstep. Tuned to the run cycle, not to a clock. */
const STRIDE = 0.78

export class RaidMode {
  private view: RoomView
  private sim: RaidSim
  private figure: Figure
  private controls: RaidControls
  private recorder: ReplayRecorder
  private panel: HTMLDivElement
  private raidTimerEl!: HTMLDivElement
  private escapeEl!: HTMLDivElement
  private captionEl!: HTMLDivElement
  private tauntEl!: HTMLDivElement
  private phase: 'intro' | 'live' | 'done' = 'intro'
  private introLeft = INTRO_SECONDS
  private captionUntil = 0
  private time = 0
  private fps = 0
  private strideLeft = STRIDE
  private wasGrounded = true

  constructor(private deps: RaidModeDeps) {
    this.view = new RoomView(deps.room)
    deps.scene.scene.add(this.view.root)
    deps.scene.setRoomSize(deps.room.width, deps.room.depth)
    deps.scene.setMode('overview')

    this.sim = new RaidSim(deps.room)
    this.figure = createFigure(RAIDER_PALETTE, { name: deps.raiderName })
    deps.scene.scene.add(this.figure.root)

    this.recorder = new ReplayRecorder(
      deps.room.id,
      encodeRoom(deps.room),
      deps.raiderName || '一个陌生人',
    )

    this.panel = el('div', { class: 'raid-ui' })
    deps.uiHost.appendChild(this.panel)
    this.renderChrome()

    this.controls = new RaidControls(deps.uiHost)
    this.controls.setEnabled(false)

    // The sounds are tiny (~56 KB total) but still fetched off the boot path; a raid is the first
    // time any of them can be needed.
    sfx.warmRaid()
    platform.gameplayStart()
  }

  private renderChrome(): void {
    this.raidTimerEl = el('div', { class: 'raid-timer' })
    this.escapeEl = el('div', { class: 'escape-timer' })
    this.captionEl = el('div', { class: 'caption' })
    this.tauntEl = el('div', { class: 'taunt-card' }, [
      el('div', { class: 'taunt-owner', text: `${this.deps.room.ownerName} 的屋子` }),
      el('div', {
        class: 'taunt-line',
        text: this.deps.room.taunt || '（他什么都没说，这更可疑。）',
      }),
    ])

    this.panel.append(
      el('div', { class: 'raid-top' }, [
        button('退出', () => this.quit(), 'btn ghost small'),
        this.raidTimerEl,
      ]),
      this.escapeEl,
      this.tauntEl,
      this.captionEl,
      el('div', { class: 'raid-help', text: '拖左半屏移动 · 点右半屏跳 · 键盘 WASD / 空格' }),
    )
  }

  update(dt: number): void {
    this.time += dt
    this.fps = this.fps === 0 ? 1 / dt : this.fps * 0.9 + (1 / dt) * 0.1

    if (this.phase === 'intro') {
      // A beat to read the room and the trash talk before you can move. This is
      // where the mind-game is set up; skipping it makes every room the same.
      this.introLeft -= dt
      if (this.introLeft <= 0) {
        this.phase = 'live'
        this.tauntEl.classList.add('taunt-away')
        this.deps.scene.setMode('follow')
        this.controls.setEnabled(true)
      }
      const snap = this.sim.snapshot()
      this.figure.update(snap.raider, dt)
      this.view.update(this.time)
      this.deps.scene.update(dt, null)
      return
    }

    if (this.phase === 'live') {
      const input = this.controls.read()
      const events = this.sim.advance(dt, input)
      this.recorder.add(events)
      for (const e of events) this.onEvent(e)

      const snap = this.sim.snapshot()
      this.recorder.observe(snap)
      this.figure.update(snap.raider, dt)
      this.footsteps(snap, dt)
      this.view.update(this.time)
      this.view.setChestVisible(!snap.raider.carrying)
      this.deps.scene.update(dt, snap.raider)

      this.raidTimerEl.textContent = `剩余 ${formatSeconds(snap.raidRemaining)}s`
      if (snap.phase === 'carrying') {
        this.escapeEl.classList.add('on')
        this.escapeEl.textContent = `门在 ${formatSeconds(snap.escapeRemaining)}s 后锁上`
        this.escapeEl.classList.toggle('urgent', snap.escapeRemaining < 4)
      } else {
        this.escapeEl.classList.remove('on', 'urgent')
      }

      if (this.time > this.captionUntil) this.captionEl.textContent = ''

      // Dev-only inspection hook: lets a browser-driving harness assert on real
      // sim state instead of guessing from pixels. Stripped from production.
      if (import.meta.env.DEV) {
        ;(window as unknown as Record<string, unknown>).__tr = { snap, fps: this.fps }
      }

      if (this.sim.finished) this.finish(snap.phase === 'escaped' ? 'escaped' : 'failed')
      return
    }

    // Done: keep the scene alive under the result card.
    const snap = this.sim.snapshot()
    this.figure.update(snap.raider, dt)
    this.view.update(this.time)
    this.deps.scene.update(dt, snap.raider)
  }

  /**
   * Footsteps, paced by distance rather than by time.
   *
   * A timer-driven step loop desynchronises the moment the raider slows down — on glue, in the air,
   * skidding across marbles — and that is exactly when the audio needs to agree with what the player
   * sees. Accumulating ground distance instead means a slow wade steps slowly and a sprint steps
   * fast, for free, and being flung makes no footsteps at all.
   */
  private footsteps(snap: SimSnapshot, dt: number): void {
    const r = snap.raider
    if (r.grounded && !this.wasGrounded) sfx.play('land')
    this.wasGrounded = r.grounded
    if (!r.grounded || r.pose === 'flung') {
      this.strideLeft = STRIDE * 0.4 // land already mid-stride so the first step is quick
      return
    }
    const moved = Math.hypot(r.vx, r.vz) * dt
    if (moved < 0.002) return
    this.strideLeft -= moved
    if (this.strideLeft <= 0) {
      this.strideLeft += STRIDE
      sfx.step()
    }
  }

  private onEvent(e: RaidEvent): void {
    const sound = EVENT_SFX[e.type]
    if (sound) sfx.play(sound)
    const caption = CAPTIONS[e.type]
    if (caption) {
      this.captionEl.textContent = caption
      this.captionEl.classList.remove('pop')
      // Force the animation to restart on repeat captions.
      void this.captionEl.offsetWidth
      this.captionEl.classList.add('pop')
      this.captionUntil = this.time + 1.6
    }
    if (e.type === 'pickup') this.view.setDoorLocked(false)
    if (e.type === 'launched' || e.type === 'escaped') platform.happy()
  }

  private finish(outcome: 'escaped' | 'failed'): void {
    if (this.phase === 'done') return
    this.phase = 'done'
    this.controls.setEnabled(false)
    // Get the HUD out of the way so the result card owns the screen.
    this.panel.classList.add('faded')
    platform.gameplayStop()
    const replay = this.recorder.finish(outcome, Date.now())
    this.deps.onFinish(outcome, replay)
  }

  private quit(): void {
    this.controls.setEnabled(false)
    platform.gameplayStop()
    this.deps.onQuit()
  }

  get escapeBudget(): number {
    return ROOM_CONFIG.escapeSeconds
  }

  dispose(): void {
    this.controls.dispose()
    this.figure.dispose()
    this.view.dispose()
    this.sim.dispose()
    this.panel.remove()
  }
}
